import { GracefulExpressionResult } from './ipr-to-logical-expressions';
import { ExpressionType, LogicalMixinExpression } from './types';

/**
 * Function type for validating if a product code is valid for mixin compilation
 */
export type ProductCodeValidator = (productCode: string) => boolean;

/**
 * Processes a LogicalMixinExpression to handle PureProductExpressions with undefined productCodes
 * (which may result e.g. from missing mapping from industries to product codes)
 *
 * Logic:
 * - PureProductExpressions with no productCode are considered invalid
 * - PureProductExpressions with invalid productCodes (e.g., phenomena or non-existent products) are considered invalid
 * - Invalid expressions are filtered from CombinationExpressions. If no products remain, the combination is invalid
 * - Binary expressions (ForIndustry, etc.) collapse to the base product if the second operand is invalid
 * - All expressions are invalid if the first/only operand is invalid
 *
 * @param expression The logical mixin expression to process
 * @param isValidProductCode Optional validator to check if a product code is valid (not a phenomenon, exists in taxonomy)
 * @returns gracefulExpression (undefined if invalid) and titles for which no mapping was found
 */
export function toGracefulLogicalExpression(
  expression: LogicalMixinExpression,
  isValidProductCode?: ProductCodeValidator,
): GracefulExpressionResult {
  const unmappedTitles: string[] = [];
  const invalidProductCodes: string[] = [];

  function processExpression(expr: LogicalMixinExpression): LogicalMixinExpression | undefined {
    switch (expr.type) {
      case ExpressionType.PureProduct: {
        if (!expr.productCode) {
          unmappedTitles.push(expr.title);
          return undefined;
        }
        // If a validator is provided, check if the product code is valid
        if (isValidProductCode && !isValidProductCode(expr.productCode)) {
          invalidProductCodes.push(expr.productCode);
          unmappedTitles.push(expr.title);
          return undefined;
        }
        return expr;
      }

      case ExpressionType.Combination: {
        const validCombinations = expr.combinations
          .map(child => processExpression(child))
          .filter((c): c is LogicalMixinExpression => c !== undefined);
        if (validCombinations.length === 0) return undefined;
        if (validCombinations.length === 1) return validCombinations[0];
        return { ...expr, combinations: validCombinations };
      }

      case ExpressionType.ForIndustry: {
        const baseProduct = processExpression(expr.baseProduct);
        if (!baseProduct) return undefined;
        const forProduct = processExpression(expr.forProduct);
        if (!forProduct) return baseProduct;
        return { ...expr, baseProduct, forProduct };
      }

      case ExpressionType.UsingEnergySource: {
        const baseProduct = processExpression(expr.baseProduct);
        if (!baseProduct) return undefined;
        const usingEnergySource = processExpression(expr.usingEnergySource);
        if (!usingEnergySource) return baseProduct;
        return { ...expr, baseProduct, usingEnergySource };
      }

      case ExpressionType.ForSpecialEndUse: {
        const baseProduct = processExpression(expr.baseProduct);
        if (!baseProduct) return undefined;
        const forSpecialEndUse = processExpression(expr.forSpecialEndUse);
        if (!forSpecialEndUse) return baseProduct;
        return { ...expr, baseProduct, forSpecialEndUse };
      }

      // Unary expressions - only have baseProduct
      case ExpressionType.Certificate:
      case ExpressionType.Recyclability:
      case ExpressionType.ForDisabledPeople: {
        const baseProduct = processExpression(expr.baseProduct);
        if (!baseProduct) return undefined;
        return { ...expr, baseProduct };
      }

      default:
        return expr;
    }
  }

  const gracefulExpression = processExpression(expression);

  // Log warnings for invalid product codes
  if (invalidProductCodes.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `Filtered out ${invalidProductCodes.length} invalid product code(s) from IPR mixin expression: ${invalidProductCodes.join(', ')}`,
    );
  }

  return {
    gracefulExpression: gracefulExpression ?? expression,
    unmappedTitles,
    isFallBack: gracefulExpression === undefined,
  };
}
